import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '../../mocks/articles';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7425ec5afee679168b1752f616a3c784.jpg';

export default function OfficePage() {
  const officeArticles = articles.filter(article => article.category === 'Office Productivity');

  const collectionPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Best Office & Productivity Tech | Work Play Save',
    description: 'Upgrade your workspace with the best productivity tech. Compare office chairs, monitors, keyboards, and tools designed for comfort and efficiency.',
    url: `${SITE_URL}/office`,
    publisher: {
      '@type': 'Organization',
      name: 'Work Play Save',
      url: SITE_URL,
    },
    about: {
      '@type': 'Thing',
      name: 'Office Productivity Technology',
    },
    hasPart: officeArticles.map((a) => ({
      '@type': 'Article',
      name: a.title,
      url: `${SITE_URL}/article/${a.slug}`,
      datePublished: a.date,
      description: a.excerpt,
    })),
  }), [officeArticles]);

  const getCategoryColor = (color: string) => {
    const colors: Record<string, string> = {
      purple: 'bg-purple-600',
      blue: 'bg-cyan-600',
      green: 'bg-emerald-600'
    };
    return colors[color] || 'bg-gray-600';
  };

  return (
    <>
      <title>Best Office &amp; Productivity Tech | Work Play Save</title>
      <meta name="description" content="Upgrade your workspace with the best productivity tech. Compare office chairs, monitors, keyboards, and tools designed for comfort and efficiency." />
      <meta name="keywords" content="office productivity tech, office chairs, productivity monitors, office keyboards, work from home setup, desk setup gear, ergonomic office gear, productivity tools" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/office`} />
      <meta property="og:title" content="Best Office &amp; Productivity Tech | Work Play Save" />
      <meta property="og:description" content="Upgrade your workspace with the best productivity tech. Compare office chairs, monitors, keyboards, and tools designed for comfort and efficiency." />
      <meta property="og:url" content={`${SITE_URL}/office`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Best Office &amp; Productivity Tech | Work Play Save" />
      <meta name="twitter:description" content="Upgrade your workspace with the best productivity tech. Compare office chairs, monitors, keyboards, and tools designed for comfort and efficiency." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLd data={collectionPageSchema} />
      <div className="min-h-screen">
        {/* Page Hero */}
        <div className="relative bg-transparent border-b border-emerald-500/20 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
            <div className="text-center space-y-6">
              <div className="inline-block px-4 py-2 bg-emerald-600 rounded-full text-sm font-semibold text-white uppercase tracking-wider mb-4">
                Office Productivity
              </div>
              <h1 className="text-6xl font-bold text-white uppercase tracking-tight">
                Office Productivity Reviews
                <div className="h-1 w-32 bg-gradient-to-r from-emerald-600 via-cyan-500 to-emerald-600 mx-auto mt-6 rounded-full shadow-[0_0_20px_rgba(16,185,129,0.6)]"></div>
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Upgrade your workspace with the best monitors, ergonomic chairs, keyboards, and productivity tools.
              </p>
            </div>
          </div>
        </div>

        {/* Tinted Section Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent shadow-[0_0_15px_rgba(16,185,129,0.3)]"></div>

        {/* Articles Grid - Tinted Section */}
        <div className="relative bg-emerald-500/8">
          <div className="max-w-7xl mx-auto px-6 py-12 relative">
            <div className="relative z-10">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white">
                  Latest Office Reviews
                  <div className="h-0.5 w-24 bg-gradient-to-r from-emerald-600 to-cyan-500 mt-3 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)]"></div>
                </h2>
                <p className="text-gray-400 mt-2">Expert reviews and buying guides for office productivity</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {officeArticles.map((article) => (
                  <Link key={article.id} to={`/article/${article.slug}`} className="group block">
                    <div className="bg-gray-900/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-emerald-500/30 hover:border-emerald-500/80 transition-all duration-300 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] h-full flex flex-col">
                      <div className="relative h-56 w-full overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/30 to-transparent"></div>
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`inline-block px-3 py-1 ${getCategoryColor(article.categoryColor)} rounded-full text-xs font-semibold text-white uppercase tracking-wider shadow-[0_0_10px_rgba(16,185,129,0.4)]`}>
                            {article.category}
                          </div>
                          <div className="text-xs text-gray-400">{article.date}</div>
                        </div>
                        <h3 className="text-xl font-bold text-white leading-tight group-hover:text-emerald-400 transition-colors duration-200 mb-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-gray-400 leading-relaxed flex-1 mb-4">{article.excerpt}</p>
                        <div className="w-full px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.3)] group-hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] transition-all duration-200">
                          Read Review
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}